import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import API_BASE_URL from './config/api';

const LockIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const ShieldIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const VENMO_HANDLE = '@nextleadercoaching';
const ZELLE_CONTACT = 'irwin@ictranco.com';

const APPEARANCE = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#2e7d8c',
    colorBackground: '#ffffff',
    colorText: '#1a2744',
    colorDanger: '#dc2626',
    fontFamily: "'Inter', system-ui, sans-serif",
    spacingUnit: '4px',
    borderRadius: '10px',
  },
  rules: {
    '.Input': {
      border: '1.5px solid #d1d9e6',
      boxShadow: 'none',
      backgroundColor: '#fafbff',
    },
    '.Input:focus': {
      border: '1.5px solid #2e7d8c',
      boxShadow: '0 0 0 3px rgba(46, 125, 140, 0.15)',
      outline: 'none',
    },
    '.Label': {
      fontWeight: '600',
      fontSize: '0.875rem',
      color: '#1a2744',
    },
    '.Tab': {
      border: '1.5px solid #d1d9e6',
      borderRadius: '10px',
    },
    '.Tab:focus': {
      boxShadow: '0 0 0 3px rgba(46, 125, 140, 0.15)',
    },
    '.Tab--selected': {
      borderColor: '#2e7d8c',
      boxShadow: '0 0 0 2px rgba(46, 125, 140, 0.2)',
    },
  },
};

function CheckoutForm({ amount, user, onPaymentSent, hideLater }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [payLaterLoading, setPayLaterLoading] = useState(false);
  const [cardError, setCardError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setCardError('');

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setCardError(submitError.message);
      setProcessing(false);
      return;
    }

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin,
      },
      redirect: 'if_required',
    });

    if (error) {
      setCardError(error.message);
      setProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      try {
        await fetch(`${API_BASE_URL}/api/onboarding/payment-sent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({ method: 'stripe' }),
        });
      } catch (_) {}
      onPaymentSent('stripe');
    }
  };

  const handlePayLater = async () => {
    setPayLaterLoading(true);
    try {
      await fetch(`${API_BASE_URL}/api/onboarding/payment-sent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({ method: 'later' }),
      });
    } catch (_) {}
    onPaymentSent('later');
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-onboarding-form">
      <div className="payment-amount-display">
        <span className="payment-amount-label">Amount due</span>
        <span className="payment-amount-value">{fmt(amount)}</span>
      </div>

      <div className="payment-accepted-cards">
        <LockIcon size={12} />
        <span>Visa · Mastercard · American Express · Discover accepted</span>
      </div>

      <div className="payment-element-wrapper">
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>

      {cardError && <p className="onboarding-error" style={{ marginTop: '0.75rem' }}>{cardError}</p>}

      <button
        type="submit"
        className="onboarding-btn payment-submit-btn"
        disabled={processing || !stripe}
        style={{ marginTop: '1.5rem' }}
      >
        {processing ? (
          'Processing…'
        ) : (
          <><LockIcon size={15} /> Pay {fmt(amount)}</>
        )}
      </button>

      <div className="payment-security-assurance">
        <ShieldIcon />
        <span>256-bit SSL encrypted · Payments processed by Stripe</span>
      </div>

      {!hideLater && (
        <>
          <div className="pay-later-divider"><span>or</span></div>
          <button
            type="button"
            className="onboarding-btn outline"
            onClick={handlePayLater}
            disabled={payLaterLoading}
          >
            {payLaterLoading ? 'Please wait…' : 'Pay Later — Continue to Intake Form'}
          </button>
          <p className="pay-later-hint">
            You can complete your payment before your first session begins.
          </p>
        </>
      )}

    </form>
  );
}

function PayLaterOnly({ user, onPaymentSent, errorMsg }) {
  const [loading, setLoading] = useState(false);

  const handlePayLater = async () => {
    setLoading(true);
    try {
      await fetch(`${API_BASE_URL}/api/onboarding/payment-sent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({ method: 'later' }),
      });
    } catch (_) {}
    onPaymentSent('later');
  };

  return (
    <div className="stripe-onboarding-form" style={{ maxWidth: 480, margin: '1.5rem auto 0' }}>
      <div className="onboarding-error" style={{ marginBottom: '1.5rem' }}>
        <p>{errorMsg}</p>
        <p style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
          Please contact your coach to set a payment amount before you can pay online.
        </p>
      </div>
      <button
        type="button"
        className="onboarding-btn outline"
        onClick={handlePayLater}
        disabled={loading}
      >
        {loading ? 'Please wait…' : 'Continue to Intake Form'}
      </button>
      <p className="pay-later-hint">
        You can complete your payment before your first session begins.
      </p>
    </div>
  );
}

function ReceiptView({ user, amount, onBack, onLogout }) {
  const paymentMethod = user.paymentMethod || user.userData?.paymentMethod;
  const methodLabel = paymentMethod === 'stripe' ? 'Credit / Debit Card'
    : paymentMethod === 'venmo' ? 'Venmo'
    : paymentMethod === 'zelle' ? 'Zelle'
    : 'Direct Payment';

  return (
    <div className="onboarding-page">
      <div className="onboarding-header">
        <a href="https://www.nextleadercoaching.com" target="_blank" rel="noopener noreferrer">
          <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" className="header-logo" />
        </a>
        <div className="header-brand-divider" />
        <span className="onboarding-step-label">Payment Receipt</span>
        {(onBack || onLogout) && (
          <div className="header-actions" style={{ marginLeft: 'auto' }}>
            {onBack && (
              <button type="button" className="header-link-btn" onClick={onBack}>
                ← Dashboard
              </button>
            )}
            {onLogout && (
              <button type="button" className="logout-btn" onClick={onLogout}>Sign Out</button>
            )}
          </div>
        )}
      </div>

      <div className="onboarding-body">
        <div className="payment-page-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✓</div>
          <h1 className="onboarding-title" style={{ marginBottom: '0.25rem' }}>Payment Confirmed</h1>
          <p className="onboarding-subtitle" style={{ marginBottom: '1.75rem' }}>
            Thank you — your coaching engagement is fully paid.
          </p>

          <div style={{ background: '#f4f7fb', borderRadius: 12, padding: '1.25rem 1.5rem', textAlign: 'left', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.65rem' }}>
              <span style={{ color: '#6b7a99', fontSize: '0.9rem' }}>Status</span>
              <span style={{ fontWeight: 600, color: '#1a7a4a' }}>Paid in full</span>
            </div>
            {amount && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.65rem' }}>
                <span style={{ color: '#6b7a99', fontSize: '0.9rem' }}>Amount</span>
                <span style={{ fontWeight: 600 }}>{fmt(amount)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7a99', fontSize: '0.9rem' }}>Method</span>
              <span style={{ fontWeight: 600 }}>{methodLabel}</span>
            </div>
          </div>

          {paymentMethod === 'stripe' && (
            <p style={{ fontSize: '0.875rem', color: '#6b7a99', marginBottom: '1.5rem' }}>
              A receipt was sent to <strong>{user.email}</strong> from Stripe.
            </p>
          )}

          {onBack && (
            <button type="button" className="client-link-btn" onClick={onBack} style={{ width: '100%' }}>
              Back to Dashboard
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PaymentPage({ user, onPaymentSent, hideLater, onBack, onLogout }) {
  const [clientSecret, setClientSecret] = useState('');
  const [loadingIntent, setLoadingIntent] = useState(true);
  const [intentError, setIntentError] = useState('');
  const [manualLoading, setManualLoading] = useState(null);

  const amount = user.userData?.contractTerms?.paymentAmount;
  const paymentConfirmed = ['stripe', 'venmo', 'zelle'].includes(user.paymentMethod || user.userData?.paymentMethod);

  const handleManualPayment = async (method) => {
    setManualLoading(method);
    try {
      await fetch(`${API_BASE_URL}/api/onboarding/payment-sent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({ method }),
      });
    } catch (_) {}
    onPaymentSent(method);
  };

  useEffect(() => {
    const createIntent = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/onboarding/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.token}`,
          },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Failed to initialize payment');
        setClientSecret(data.clientSecret);
      } catch (err) {
        setIntentError(err.message);
      } finally {
        setLoadingIntent(false);
      }
    };
    createIntent();
  }, [user.token]);

  if (paymentConfirmed) {
    return <ReceiptView user={user} amount={amount} onBack={onBack} onLogout={onLogout} />;
  }

  return (
    <div className="onboarding-page">
      <div className="onboarding-header">
        <a href="https://www.nextleadercoaching.com" target="_blank" rel="noopener noreferrer">
          <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" className="header-logo" />
        </a>
        <div className="header-brand-divider" />
        <span className="onboarding-step-label">Step 2 of 4 — Payment</span>
        {(onBack || onLogout) && (
          <div className="header-actions" style={{ marginLeft: 'auto' }}>
            {onBack && (
              <button type="button" className="header-link-btn" onClick={onBack}>
                ← Dashboard
              </button>
            )}
            {onLogout && (
              <button type="button" className="logout-btn" onClick={onLogout}>Sign Out</button>
            )}
          </div>
        )}
      </div>

      <div className="onboarding-body">
        <div className="payment-page-card">
          <h1 className="onboarding-title">Complete Your Payment</h1>
          <p className="onboarding-subtitle">
            Enter your payment details below to complete your coaching enrollment.
          </p>

          {loadingIntent && <p className="onboarding-loading">Preparing payment form…</p>}

          {!loadingIntent && intentError && (
            <PayLaterOnly user={user} onPaymentSent={onPaymentSent} errorMsg={intentError} />
          )}

          {!loadingIntent && !intentError && clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret, appearance: APPEARANCE }}>
              <CheckoutForm amount={amount} user={user} onPaymentSent={onPaymentSent} hideLater={hideLater} />
            </Elements>
          )}

          {!loadingIntent && amount && (
            <>
              <div className="pay-later-divider" style={{ margin: '1.25rem 0 1rem' }}>
                <span>or pay directly</span>
              </div>
              <div className="manual-payment-options">
                <div className="manual-payment-card">
                  <div className="manual-payment-brand venmo-brand">
                    <span className="manual-payment-icon">V</span>
                    <span>Venmo</span>
                  </div>
                  <p className="manual-payment-handle">{VENMO_HANDLE}</p>
                  <p className="manual-payment-instruction">Send {fmt(amount)}, then tap the button below</p>
                  <button
                    type="button"
                    className="onboarding-btn manual-payment-btn"
                    onClick={() => handleManualPayment('venmo')}
                    disabled={manualLoading !== null}
                  >
                    {manualLoading === 'venmo' ? 'Please wait…' : "I've Sent Payment"}
                  </button>
                </div>

                <div className="manual-payment-card">
                  <div className="manual-payment-brand zelle-brand">
                    <span className="manual-payment-icon">Z</span>
                    <span>Zelle</span>
                  </div>
                  <p className="manual-payment-handle">{ZELLE_CONTACT}</p>
                  <p className="manual-payment-instruction">Send {fmt(amount)}, then tap the button below</p>
                  <button
                    type="button"
                    className="onboarding-btn manual-payment-btn"
                    onClick={() => handleManualPayment('zelle')}
                    disabled={manualLoading !== null}
                  >
                    {manualLoading === 'zelle' ? 'Please wait…' : "I've Sent Payment"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <p className="payment-page-help">
          Questions about your payment? Contact <a href="mailto:irwin@nextleadercoaching.com">irwin@nextleadercoaching.com</a>
        </p>
      </div>
    </div>
  );
}

export default PaymentPage;
